/**
 * @file OrdersTable.tsx
 *
 * List-view table for orders: order number, status badge, date + time, customer,
 * payment method, total, and a per-row actions trigger that opens the contextual
 * menu (View / Edit / Delete). Clicking a row opens the details drawer. Columns
 * collapse on smaller viewports via Gravity column-hiding utilities. Presentational:
 * selection, the menu, and data live in {@link OrdersView}.
 */

import { Icon } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { StatusBadge } from '../components/StatusBadge';
import type { ShopOrder } from '../api/shop-types';
import { customerName, formatOrderDate, formatOrderTime } from './order-list';
import { statusIndicator, statusLabel } from './order-status';
import styles from './OrdersView.module.css';

const TABLE_CLASS = [
  'gv-col-6-text-right',
  'gv-max-tab-col-5-hidden',
  'gv-max-mob-col-3-hidden',
  'gv-max-mob-col-4-hidden',
].join(' ');

interface OrdersTableProps {
  orders: ShopOrder[];
  currencySymbol: string;
  selectedId: string | null;
  menuOrderId: string | null;
  onSelect: (order: ShopOrder) => void;
  onMore: (order: ShopOrder, triggerEl: HTMLButtonElement) => void;
}

/**
 * Renders the orders list table.
 *
 * @param props - The orders, currency symbol, the selected/menu order ids, and
 *   the row select / menu handlers.
 *
 * @returns The table element.
 */
export function OrdersTable({
  orders,
  currencySymbol,
  selectedId,
  menuOrderId,
  onSelect,
  onMore,
}: OrdersTableProps) {
  const intl = useIntl();

  return (
    <table className={TABLE_CLASS} data-testid="orders-table">
      <thead>
        <tr>
          <th scope="col">
            <FormattedMessage {...adminMessages.ordersColNumber} />
          </th>
          <th scope="col">
            <FormattedMessage {...adminMessages.ordersColStatus} />
          </th>
          <th scope="col">
            <FormattedMessage {...adminMessages.ordersColDate} />
          </th>
          <th scope="col">
            <FormattedMessage {...adminMessages.ordersColCustomer} />
          </th>
          <th scope="col">
            <FormattedMessage {...adminMessages.ordersColPayment} />
          </th>
          <th scope="col">
            <FormattedMessage {...adminMessages.ordersColTotal} />
          </th>
          <th scope="col" aria-hidden="true" />
        </tr>
      </thead>
      <tbody>
        {orders.map((order) => {
          const labelDesc = statusLabel(order.status);
          const label = labelDesc ? intl.formatMessage(labelDesc) : order.status;
          return (
            <tr
              key={order.id}
              style={{ cursor: 'pointer' }}
              data-testid="order-row"
              data-id={order.id}
              aria-selected={order.id === selectedId || undefined}
              className={order.id === selectedId ? 'gv-active' : undefined}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={() => onSelect(order)}
            >
              <td>
                <span className="gv-text-sm">{order.number || '—'}</span>
              </td>
              <td>
                <StatusBadge indicator={statusIndicator(order.status)} label={label} />
              </td>
              <td>
                <div className="gv-text-sm">{formatOrderDate(order.date_created)}</div>
                <div className={`gv-caption-sm ${styles.muted}`}>
                  {formatOrderTime(order.date_created)}
                </div>
              </td>
              <td>
                <span className="gv-text-sm">{customerName(order)}</span>
              </td>
              <td>
                <span className="gv-text-sm">{order.payment_method_title || '—'}</span>
              </td>
              <td>
                <span className="gv-text-sm">
                  {order.currency_symbol || currencySymbol} {order.total}
                </span>
              </td>
              <td>
                <button
                  className="gv-button gv-button-icon"
                  type="button"
                  data-testid="order-row-menu"
                  aria-label={intl.formatMessage(adminMessages.ordersActions)}
                  aria-expanded={menuOrderId === order.id}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    onMore(order, event.currentTarget);
                  }}
                >
                  <Icon name="more_horiz" size="small" />
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
